import { Navigate, Route, Routes } from "react-router-dom";
import { PatientShell } from "./components/layout/PatientShell";
import { PublicShell } from "./components/layout/PublicShell";
import { AdminShell } from "./components/layout/AdminShell";
import { RequireAuth, RedirectIfAuthenticated } from "./components/RequireAuth";
import { DesignPreview } from "./routes/DesignPreview";
import { CardVerify } from "./routes/CardVerify";
import { ChatScreen } from "./routes/ChatScreen";
import { LandingScreen } from "./routes/LandingScreen";
import { TriageScreen } from "./routes/TriageScreen";
import { ReservationLayout, ReservationGate } from "./routes/ReservationScreen";
import { AdminLogin, PatientLogin } from "./routes/auth/LoginScreen";
import { RegisterScreen } from "./routes/auth/RegisterScreen";
import { PasswordRecoveryScreen } from "./routes/auth/PasswordRecoveryScreen";
import { PatientDashboard } from "./routes/patient/PatientDashboard";
import { HealthCheckWizard } from "./routes/patient/HealthCheckWizard";
import { HealthCheckResult } from "./routes/patient/HealthCheckResult";
import { MriAssist } from "./routes/patient/MriAssist";
import {
  PatientHealthHub,
  PatientMedications,
  PatientNotifications,
  PatientProfile,
  PatientSecurity,
} from "./routes/patient/PatientScreens";
import { AdminAiModels } from "./routes/admin/AdminAiModels";
import { AdminOverview } from "./routes/admin/AdminOverview";
import { HealthMonitoring } from "./routes/admin/HealthMonitoring";
import {
  AdminAppointments,
  AdminConversations,
  AdminDoctors,
  AdminKnowledge,
  AdminPatients,
  AdminUsers,
} from "./routes/admin/AdminLists";
import {
  AdminAnalytics,
  AdminBroadcast,
  AdminIntelligence,
  AdminReports,
} from "./routes/admin/AdminInsights";
import { AdminLiveChat, AdminPatientLabs } from "./routes/admin/AdminLiveChat";
import {
  AdminHealthPatient,
  AdminHealthSession,
  AdminProfile,
  PatientSubscription,
} from "./routes/MiscScreens";

/**
 * Route table for the whole product. Every page route the Flask app serves now
 * has a real screen behind it. Guards replace Flask's `login_required`
 * redirects, and the three shells (public, patient, admin) supply the
 * navigation each audience needs.
 */
export function App() {
  return (
    <Routes>
      {/* Design system reference. Kept inside the app so the tokens it shows
          are the ones the product actually renders, not a drifting copy. */}
      <Route path="/preview/design" element={<DesignPreview />} />

      {/* Scanned from the QR on a patient card, on a counter device that is
          not signed in — public by design. */}
      <Route path="/kartu/:token" element={<CardVerify />} />

      {/* --- Auth --- */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <AdminLogin />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/patient/login"
        element={
          <RedirectIfAuthenticated>
            <PatientLogin />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/patient/register"
        element={
          <RedirectIfAuthenticated>
            <RegisterScreen />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/forgot-password" element={<PasswordRecoveryScreen />} />
      <Route path="/reset-password" element={<PasswordRecoveryScreen />} />

      {/* --- Patient portal --- */}
      <Route
        path="/patient"
        element={
          <RequireAuth as="patient">
            <PatientShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/patient/dashboard" replace />} />
        <Route path="dashboard" element={<PatientDashboard />} />
        <Route path="health-check" element={<HealthCheckWizard />} />
        <Route path="health-check/result/:id" element={<HealthCheckResult />} />
        <Route path="health-hub" element={<PatientHealthHub />} />
        <Route path="chat" element={<ChatScreen />} />
        <Route path="mri" element={<MriAssist />} />
        {/* Triage and reservation are also public routes, but a signed-in
            patient reaches them from the sidebar — sending them to the public
            shell dropped the sidebar and left no way back into the portal. */}
        <Route path="triage" element={<TriageScreen />} />
        <Route path="reservasi" element={<ReservationLayout />} />
        <Route path="medications" element={<PatientMedications />} />
        <Route path="notifications" element={<PatientNotifications />} />
        <Route path="profile" element={<PatientProfile />} />
        <Route path="security" element={<PatientSecurity />} />
        <Route path="subscription" element={<PatientSubscription />} />
      </Route>

      {/* --- Admin panel --- */}
      <Route
        path="/admin"
        element={
          <RequireAuth as="admin">
            <AdminShell />
          </RequireAuth>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="health-monitoring" element={<HealthMonitoring />} />
        <Route path="health-monitoring/patient/:rm" element={<AdminHealthPatient />} />
        <Route path="health-monitoring/session/:id" element={<AdminHealthSession />} />
        <Route path="livechat" element={<AdminLiveChat />} />
        <Route path="conversations" element={<AdminConversations />} />
        <Route path="appointments" element={<AdminAppointments />} />
        <Route path="patients" element={<AdminPatients />} />
        <Route path="patients/:rm/labs" element={<AdminPatientLabs />} />
        <Route path="doctors" element={<AdminDoctors />} />
        <Route path="knowledge" element={<AdminKnowledge />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="intelligence" element={<AdminIntelligence />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="ai-models" element={<AdminAiModels />} />
        <Route path="profile" element={<AdminProfile />} />
        {/* Same reason as triage in the patient portal: "Buka Chatbot" is a
            sidebar item, and sending it to the public route dropped the admin
            out of the panel mid-task. */}
        <Route path="chatbot" element={<ChatScreen />} />
      </Route>

      {/* --- Public --- */}
      {/* Public routes share a header so the chatbot is no longer a dead end
          with no route to login, registration, or the other services. */}
      <Route element={<PublicShell />}>
        {/* Landing, bukan chatbot. Pengunjung baru dulu mendapat kolom
            ketik tanpa penjelasan layanan apa pun. */}
        <Route path="/" element={<LandingScreen />} />
        <Route path="/chat" element={<ChatScreen />} />
        <Route path="/triage" element={<TriageScreen />} />
        {/* Reservation writes to the patient record, so it needs an account;
            triage is deliberately open to anyone. */}
        <Route
          path="/reservasi"
          element={
            <RequireAuth as="patient" fallback={<ReservationGate />}>
              <ReservationLayout />
            </RequireAuth>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
